import React, { useState } from "react";
import { useParams } from "react-router-dom";

const translations = {
  english: {
    title: "Rate Your Purchase!",
    desc: "Your input matters! Let us know if your recent purchase met your expectations.",
    name: "Your Name",
    email: "Email Address",
    rating: "Rating",
    feedback: "Your Feedback",
    agree: "I agree to share my feedback",
    button: "Write Feedback",
    success: "🎉 Thank you! Your feedback has been submitted successfully."
  },
  arabic: {
    title: "قيم عملية الشراء!",
    desc: "رأيك يهمنا.",
    name: "اسمك",
    email: "البريد الإلكتروني",
    rating: "التقييم",
    feedback: "ملاحظاتك",
    agree: "أوافق على مشاركة رأيي",
    button: "إرسال",
    success: "🎉 شكراً لملاحظاتك!"
  },
  hindi: {
    title: "अपनी खरीद को रेट करें!",
    desc: "आपकी राय हमारे लिए महत्वपूर्ण है।",
    name: "आपका नाम",
    email: "ईमेल",
    rating: "रेटिंग",
    feedback: "आपकी प्रतिक्रिया",
    agree: "मैं सहमत हूँ",
    button: "सबमिट करें",
    success: "🎉 धन्यवाद! आपकी प्रतिक्रिया प्राप्त हुई।"
  },
  french: {
    title: "Évaluez votre achat!",
    desc: "Votre avis est important pour nous.",
    name: "Votre nom",
    email: "Email",
    rating: "Note",
    feedback: "Votre avis",
    agree: "J'accepte de partager mon avis",
    button: "Soumettre",
    success: "🎉 Merci pour votre avis!"
  },
  german: {
    title: "Bewerten Sie Ihren Kauf!",
    desc: "Ihre Meinung ist uns wichtig.",
    name: "Ihr Name",
    email: "E-Mail-Adresse",
    rating: "Bewertung",
    feedback: "Ihr Feedback",
    agree: "Ich stimme zu",
    button: "Absenden",
    success: "🎉 Vielen Dank für Ihr Feedback!"
  },
  spanish: {
    title: "¡Califica tu compra!",
    desc: "Tu opinión es importante para nosotros.",
    name: "Tu nombre",
    email: "Correo electrónico",
    rating: "Calificación",
    feedback: "Tu opinión",
    agree: "Acepto compartir mi opinión",
    button: "Enviar",
    success: "🎉 ¡Gracias por tu opinión!"
  }
};

const WriteFeedback = () => {
  const { language } = useParams();
  const t = translations[language] || translations.english;

  const [form, setForm] = useState({
    name: "",
    email: "",
    rating: "",
    feedback: "",
    agree: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [validated, setValidated] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!form.name || !form.email || !form.rating || !form.feedback || !form.agree) {
      setValidated(true);
      return;
    }

    setSubmitted(true);
  };

  return (
    <div dir={language === "arabic" ? "rtl" : "ltr"}>
      <div className="w-100 p-0 shadow">
        <div>

          {/* Header */}
          <div className="p-4 text-white bg-dark">
            <h3 className="mb-1 fw-bold">{t.title}</h3>
            <p className="mb-0">{t.desc}</p>
          </div>

          {/* Body */}
          <div className="p-4 bg-light">

            {/* Success Message */}
            {submitted && (
              <div className="alert alert-success text-center">
                {t.success}
              </div>
            )}

            {!submitted && (
              <form noValidate>

                {/* Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t.name}</label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control rounded-3 ${validated && !form.name ? "is-invalid" : ""}`}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">Please enter your name</div>
                </div>

                {/* Email */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t.email}</label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control rounded-3 ${validated && !form.email ? "is-invalid" : ""}`}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">Please enter a valid email</div>
                </div>

                {/* Rating */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t.rating}</label>
                  <select
                    name="rating"
                    className={`form-select rounded-3 ${validated && !form.rating ? "is-invalid" : ""}`}
                    onChange={handleChange}
                  >
                    <option value="">Select rating</option>
                    <option value="1">⭐ 1 - Poor</option>
                    <option value="2">⭐⭐ 2 - Fair</option>
                    <option value="3">⭐⭐⭐ 3 - Good</option>
                    <option value="4">⭐⭐⭐⭐ 4 - Very Good</option>
                    <option value="5">⭐⭐⭐⭐⭐ 5 - Excellent</option>
                  </select>
                  <div className="invalid-feedback">Please select a rating</div>
                </div>

                {/* Feedback */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t.feedback}</label>
                  <textarea
                    name="feedback"
                    rows="4"
                    className={`form-control rounded-3 ${validated && !form.feedback ? "is-invalid" : ""}`}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">Please write your feedback</div>
                </div>

                {/* Checkbox */}
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    name="agree"
                    className={`form-check-input ${validated && !form.agree ? "is-invalid" : ""}`}
                    onChange={handleChange}
                  />
                  <label className="form-check-label">{t.agree}</label>
                  <div className="invalid-feedback">You must agree before submitting</div>
                </div>

                {/* Button */}
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="btn w-100 text-dark rounded-3 bg-warning"
                >
                  {t.button}
                </button>

              </form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default WriteFeedback;